import { Floppy2, X } from "react-bootstrap-icons";
import { useForm } from "../../Hook/Form/useForm";
import { Button, Form as BootstrapForm } from "react-bootstrap";
import { Field } from "./field";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";

export default function Form(props) {
    const {
        sections = [], // Default para array vazio
        submitText,
        cancelText,
        maxWidth = "lg",
        onSubmit,
        onCancel,
        isLoading: externalLoading = false,
        data = {},
        errors: externalErrors = {},
        validateOnChange = false,
        validateOnBlur = true,
        onChange,
    } = props;

    const { t } = useTranslation();
    const isMobile = useMediaQuery({ maxWidth: 767 });

    // Extrair todos os campos de todas as seções
    const allFields = (sections || []).flatMap(
        (section) => section?.fields || []
    );

    // Usar o hook de formulário
    const {
        formData,
        errors: hookErrors,
        isLoading: hookLoading,
        updateField,
        validateField,
        validateForm,
        getFieldValue,
    } = useForm(allFields, { initialData: data, onDataChange: onChange });

    // Verificação de segurança após os hooks
    if (!sections || !Array.isArray(sections)) {
        console.warn("Form: sections deve ser um array válido");
        return <div>Erro na configuração do formulário</div>;
    }

    // Combinar erros externos com erros do hook
    const errors = { ...hookErrors, ...externalErrors };
    const isLoading = externalLoading || hookLoading;

    // Handler para submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            await onSubmit(formData);
        } catch (error) {
            console.error("Erro ao submeter formulário:", error);
        }
    };

    // Handler para mudança de campo
    const handleFieldChange = (fieldName, value) => {
        updateField(fieldName, value);

        if (validateOnChange) {
            setTimeout(() => validateField(fieldName), 0);
        }
    };

    // Handler para blur de campo
    const handleFieldBlur = (fieldName) => {
        if (validateOnBlur) {
            validateField(fieldName);
        }
    };

    // Determinar classes de largura máxima
    const getMaxWidthClass = () => {
        switch (maxWidth) {
            case "sm":
                return "container-sm";
            case "md":
                return "container-md";
            case "lg":
                return "container-lg";
            case "xl":
                return "container-xl";
            case "2xl":
                return "container-xxl";
            case "full":
                return "w-100";
            default:
                return "container-lg";
        }
    };

    // Determinar classes de grid baseado no número de colunas
    const getGridClasses = (columns = 2) => {
        switch (columns) {
            case 1:
                return "row-cols-1";
            case 2:
                return "row-cols-1 row-cols-md-2";
            case 3:
                return "row-cols-1 row-cols-md-2 row-cols-lg-3";
            case 4:
                return "row-cols-1 row-cols-md-2 row-cols-lg-4";
            default:
                return "row-cols-1 row-cols-md-2";
        }
    };

    // Verificar se um campo deve ser exibido
    const shouldShowField = (field) => {
        if (!field.condition) return true;
        return field.condition(formData);
    };
    return (
        <div className={`mx-auto p-4 ${getMaxWidthClass()} w-100`}>
            {/* Formulário */}
            <BootstrapForm
                onSubmit={handleSubmit}
                className="d-flex flex-column gap-4"
                autoComplete="off"
            >
                {/* Campos honeypot para confundir o autocomplete dos navegadores */}
                <div style={{ display: "none" }}>
                    <input
                        type="text"
                        name="username"
                        autoComplete="username"
                        tabIndex={-1}
                    />
                    <input
                        type="email"
                        name="fake_email"
                        autoComplete="email"
                        tabIndex={-1}
                    />
                    <input
                        type="password"
                        name="fake_password"
                        autoComplete="current-password"
                        tabIndex={-1}
                    />
                </div>

                {sections.map((section, sectionIndex) => (
                    <div
                        key={sectionIndex}
                        className={`card shadow-lg border-0 bg-body-tertiary w-100 ${
                            section.className || ""
                        }`}
                        data-bs-theme="auto"
                    >
                        <div className="card-body p-4">
                            {/* Título da seção */}
                            <div className="mb-4">
                                <h2 className="card-title h5 d-flex align-items-center mb-2 text-dark text-light-emphasis">
                                    {section.icon && (
                                        <section.icon
                                            className="me-3 text-primary"
                                            style={{
                                                width: "1.5rem",
                                                height: "1.5rem",
                                            }}
                                        />
                                    )}
                                    {section.title}
                                </h2>
                                {section.description && (
                                    <p className="card-text text-muted text-secondary-emphasis">
                                        {section.description}
                                    </p>
                                )}
                            </div>

                            {/* Campos da seção */}
                            <div
                                className={`row g-4 ${getGridClasses(
                                    section.columns
                                )}`}
                            >
                                {section.fields
                                    .filter(shouldShowField)
                                    .map((field) => (
                                        <div key={field.name} className="col">
                                            <Field
                                                config={field}
                                                value={getFieldValue(
                                                    field.name
                                                )}
                                                error={errors[field.name]}
                                                onChange={(value) =>
                                                    handleFieldChange(
                                                        field.name,
                                                        value
                                                    )
                                                }
                                                onBlur={() =>
                                                    handleFieldBlur(field.name)
                                                }
                                            />
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                ))}

                {/* Ações */}
                <div className="d-flex flex-column flex-sm-row justify-content-end gap-3 pt-3">
                    <Button
                        variant="outline-secondary"
                        onClick={onCancel}
                        className={`${isMobile ? "w-100" : "w-sm-auto"}`}
                    >
                        <X className="me-1" style={{ fontSize: "1.5rem" }} />
                        {cancelText || t("common.actions.cancel")}
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        className={`${isMobile ? "w-100" : "w-sm-auto"}`}
                    >
                        {isLoading ? (
                            <span className="d-flex align-items-center">
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                {submitText || t("common.actions.save")}
                            </span>
                        ) : (
                            <>
                                <Floppy2 className="me-2" />
                                <span>
                                    {submitText || t("common.actions.save")}
                                </span>
                            </>
                        )}
                    </Button>
                </div>
            </BootstrapForm>
        </div>
    );
}
